import { useState } from 'react';
import type { PolicyTab } from '../../../content/policiesContent';
import { audienceFromSignupRole, signupRoleFromAudience } from '../../../content/authFeatureShowcase';
import { PLANTATION_SUB_ROLE_OPTIONS, SIGNUP_ROLE_OPTIONS } from '../../../config/audience';
import type { SignupLocationRow } from '../../../services/signupLocations';
import { useAuth } from './AuthContext';
import PoliciesTermsModal from './PoliciesTermsModal';
import SignupLocationSearch, { type LguMonitoringLevel } from './SignupLocationSearch';
import {
  AuthButton,
  AuthError,
  AuthInput,
  AuthPageShell,
  AuthPolicyFooter,
  AuthSelect,
} from './AuthPageLayout';
import { AUTH_GREEN, AUTH_GREEN_HOVER, AUTH_LINE, AUTH_MUTED, AUTH_SURFACE } from './authTheme';

interface SignupProps {
  onSwitchToLogin: () => void;
  onVerificationRequired: (email: string) => void;
}

const PENDING_SIGNUP_KEY = 'digisaka_pending_signup';

const LGU_LEVEL_OPTIONS: { value: LguMonitoringLevel; label: string }[] = [
  { value: 'province', label: 'Province' },
  { value: 'municipality', label: 'Municipality / city' },
  { value: 'barangay', label: 'Barangay' },
];

/** Normalize PH mobile to 09XXXXXXXXX when possible. */
export function normalizePhMobile(input: string): string {
  const digits = String(input || '').replace(/\D/g, '');
  if (digits.startsWith('63') && digits.length >= 12) return `0${digits.slice(2, 12)}`;
  if (digits.length === 10 && digits.startsWith('9')) return `0${digits}`;
  if (digits.length === 11 && digits.startsWith('09')) return digits;
  return digits;
}

function locationId(row: SignupLocationRow | null): string {
  if (!row) return '';
  return String(row.code || row.id || '').trim();
}

export default function Signup({ onSwitchToLogin, onVerificationRequired }: SignupProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const [role, setRole] = useState('blo');
  const [subRole, setSubRole] = useState('banana');
  const [organization, setOrganization] = useState('');
  const [showTrialCelebration, setShowTrialCelebration] = useState(false);
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState('');
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);
  const [policiesOpen, setPoliciesOpen] = useState(false);
  const [policiesTab, setPoliciesTab] = useState<PolicyTab>('privacy');

  // LGU: one monitoring level + one area (no parent cascade required)
  const [lguLevel, setLguLevel] = useState<LguMonitoringLevel>('municipality');
  const [lguArea, setLguArea] = useState<SignupLocationRow | null>(null);

  // Carbon (pd_carbon): province_id + city_id
  const [carbonProvince, setCarbonProvince] = useState<SignupLocationRow | null>(null);
  const [carbonCity, setCarbonCity] = useState<SignupLocationRow | null>(null);

  const openPolicies = (tab: PolicyTab) => {
    setPoliciesTab(tab);
    setPoliciesOpen(true);
  };

  const resetLocationState = () => {
    setLguLevel('municipality');
    setLguArea(null);
    setCarbonProvince(null);
    setCarbonCity(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!acceptedPolicies) {
      setError('Please accept the Privacy Policy and Terms & Conditions to create an account.');
      return;
    }

    const normalizedMobile = normalizePhMobile(mobile);
    if (normalizedMobile.length !== 11 || !normalizedMobile.startsWith('09')) {
      setError('Enter a valid Philippine mobile number (e.g. 09171234567).');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (role === 'plantation_owner' && !subRole) {
      setError('Select a crop (banana or coconut).');
      return;
    }

    if (role === 'lgu' && !lguArea) {
      setError('Search and select one monitoring area for your LGU account.');
      return;
    }

    if (role === 'project_developer' && (!carbonProvince || !carbonCity)) {
      setError('Province and municipality are required for Carbon accounts.');
      return;
    }

    setLoading(true);

    try {
      const options: {
        role: string;
        sub_role?: string;
        organization?: string;
        mobile: string;
        password_confirmation: string;
        agree_policy: boolean;
        monitoring_level?: LguMonitoringLevel;
        province?: string;
        province_id?: string;
        municipality?: string;
        city_id?: string;
        barangay?: string;
        barangay_id?: string;
      } = {
        role,
        sub_role: role === 'plantation_owner' ? subRole : undefined,
        organization: organization.trim() || undefined,
        mobile: normalizedMobile,
        password_confirmation: confirmPassword,
        agree_policy: true,
      };

      if (role === 'lgu' && lguArea) {
        options.monitoring_level = lguLevel;
        const id = locationId(lguArea);
        const areaName = (lguArea.name || '').trim();
        if (lguLevel === 'province') {
          options.province = areaName || undefined;
          options.province_id = id;
        } else if (lguLevel === 'municipality') {
          options.municipality = areaName || undefined;
          options.city_id = id;
        } else {
          options.barangay = areaName || undefined;
          options.barangay_id = String(lguArea.id);
        }
      }

      if (role === 'project_developer' && carbonProvince && carbonCity) {
        options.province_id = locationId(carbonProvince);
        options.city_id = locationId(carbonCity);
      }

      const result = await signup(name, email, password, options);
      if (result.requiresVerification) {
        try {
          sessionStorage.setItem(
            PENDING_SIGNUP_KEY,
            JSON.stringify({
              email,
              password,
              full_name: name,
              ...options,
            })
          );
        } catch {
          // ignore quota errors
        }
        setPendingVerifyEmail(email);
        setShowTrialCelebration(true);
      }
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { detail?: unknown; message?: unknown } };
        message?: string;
      };
      const data = ax.response?.data;
      const errorDetail = data?.detail;

      if (errorDetail === 'The user with this email already exists in the system.') {
        setError('An account with this email already exists. Please login instead.');
      } else if (typeof errorDetail === 'string' && errorDetail.trim()) {
        // Prefer exact FastAPI / digisaka.app detail text.
        setError(errorDetail.trim());
      } else if (Array.isArray(errorDetail)) {
        const validationErrors = errorDetail
          .map((e: { msg?: string; message?: string } | string) =>
            typeof e === 'string' ? e : e?.msg || e?.message || ''
          )
          .map((s) => String(s).trim())
          .filter(Boolean)
          .join('; ');
        setError(validationErrors || 'Please check your input and try again.');
      } else if (typeof data?.message === 'string' && data.message.trim()) {
        setError(data.message.trim());
      } else if (ax.message?.trim()) {
        setError(ax.message.trim());
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const dismissCelebrationAndVerify = () => {
    setShowTrialCelebration(false);
    const em = pendingVerifyEmail;
    setPendingVerifyEmail('');
    if (em) onVerificationRequired(em);
  };

  const lguSearchKind =
    lguLevel === 'province'
      ? 'provinces'
      : lguLevel === 'municipality'
        ? 'municipalities'
        : 'barangays';

  return (
    <>
      <PoliciesTermsModal
        isOpen={policiesOpen}
        initialTab={policiesTab}
        onClose={() => setPoliciesOpen(false)}
      />

      {showTrialCelebration && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-md"
          role="dialog"
          aria-modal="true"
          aria-labelledby="signup-celebration-title"
        >
          <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-[#00854020]">
            <div
              className="relative px-6 pb-10 pt-9 text-center text-white"
              style={{ background: `linear-gradient(to bottom right, ${AUTH_GREEN}, ${AUTH_GREEN_HOVER}, #064e3b)` }}
            >
              <div className="relative mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 shadow-lg ring-2 ring-white/30 backdrop-blur-sm">
                <i className="ri-gift-2-fill text-4xl text-white" aria-hidden />
              </div>
              <h2 id="signup-celebration-title" className="relative text-2xl font-extrabold tracking-tight">
                Welcome to Digisaka!
              </h2>
              <p className="relative mt-2 text-sm font-medium text-white/80">
                Your account is on the <span className="font-bold text-white">Daily</span> plan
              </p>
            </div>
            <div className="space-y-4 px-6 pb-7 pt-6 text-center">
              <p className="text-[15px] leading-relaxed text-slate-600">
                You&apos;re automatically given{' '}
                <strong className="font-semibold" style={{ color: AUTH_GREEN }}>
                  1 month free
                </strong>{' '}
                on our{' '}
                <strong className="font-semibold" style={{ color: AUTH_GREEN }}>
                  Daily analysis pack
                </strong>
                .
              </p>
              <p className="text-xs leading-relaxed text-slate-500">
                We&apos;ve emailed a verification code (valid 10 minutes). Enter it next to activate
                your account. After that you can also sign in with your mobile number.
              </p>
              <AuthButton type="button" onClick={dismissCelebrationAndVerify}>
                Continue to verify email
              </AuthButton>
            </div>
          </div>
        </div>
      )}

      <AuthPageShell
        title="Create your account"
        subtitle="1 month free on the Daily analysis pack."
        badge="Free trial"
        fitContentScale={false}
        activeAudience={audienceFromSignupRole(role)}
        plantationCrop={subRole === 'coconut' ? 'coconut' : 'banana'}
        onAudienceChange={(id) => {
          setRole(signupRoleFromAudience(id));
          if (id === 'plantation') setSubRole('banana');
          resetLocationState();
        }}
        onPlantationCropChange={(crop) => setSubRole(crop)}
        footer={
          <AuthPolicyFooter
            switchPrompt="Already have an account?"
            switchLabel="Sign in"
            onSwitch={onSwitchToLogin}
            onOpenPrivacy={() => openPolicies('privacy')}
            onOpenTerms={() => openPolicies('terms')}
          />
        }
      >
        {error && <AuthError message={error} />}

        <form onSubmit={handleSubmit} className="space-y-6">
          <section className="space-y-5">
            <AuthInput
              id="name"
              label="Full name"
              icon="ri-user-line"
              value={name}
              onChange={setName}
              placeholder="Juan Dela Cruz"
              autoComplete="name"
              required
            />
            <AuthInput
              id="email"
              label="Email address"
              type="email"
              icon="ri-mail-line"
              value={email}
              onChange={setEmail}
              placeholder="you@example.com"
              autoComplete="email"
              required
            />
            <AuthInput
              id="mobile"
              label="Mobile number"
              type="tel"
              icon="ri-smartphone-line"
              value={mobile}
              onChange={setMobile}
              placeholder="09171234567"
              autoComplete="tel"
              required
              hint="Philippine numbers only. After verification you can also sign in with this number."
            />
          </section>

          <section className="space-y-5">
            <AuthSelect
              id="role"
              label="Account type"
              value={role}
              onChange={(v) => {
                setRole(v);
                resetLocationState();
              }}
              options={SIGNUP_ROLE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            />
            {role === 'plantation_owner' ? (
              <AuthSelect
                id="sub-role"
                label="Crop"
                value={subRole}
                onChange={setSubRole}
                options={PLANTATION_SUB_ROLE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              />
            ) : null}
            <AuthInput
              id="organization"
              label="Organization"
              icon="ri-building-4-line"
              value={organization}
              onChange={setOrganization}
              placeholder="Optional"
              autoComplete="organization"
              maxLength={500}
            />
          </section>

          {role === 'lgu' ? (
            <section
              className="space-y-5 rounded-xl border p-4"
              style={{ borderColor: AUTH_LINE, background: AUTH_SURFACE }}
            >
              <p
                className="text-xs font-semibold uppercase tracking-[0.15em]"
                style={{ color: AUTH_GREEN }}
              >
                Monitoring area
              </p>
              <AuthSelect
                id="lgu-level"
                label="Monitoring level"
                value={lguLevel}
                onChange={(v) => {
                  setLguLevel(v as LguMonitoringLevel);
                  setLguArea(null);
                }}
                options={LGU_LEVEL_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              />
              <SignupLocationSearch
                key={lguLevel}
                kind={lguSearchKind}
                label={
                  lguLevel === 'province'
                    ? 'Province'
                    : lguLevel === 'municipality'
                      ? 'Municipality / city'
                      : 'Barangay'
                }
                selected={lguArea}
                onSelect={setLguArea}
                placeholder="Search by name…"
              />
            </section>
          ) : null}

          {role === 'project_developer' ? (
            <section
              className="space-y-5 rounded-xl border p-4"
              style={{ borderColor: AUTH_LINE, background: AUTH_SURFACE }}
            >
              <p
                className="text-xs font-semibold uppercase tracking-[0.15em]"
                style={{ color: AUTH_GREEN }}
              >
                Project location
              </p>
              <SignupLocationSearch
                kind="provinces"
                label="Province"
                selected={carbonProvince}
                onSelect={(row) => {
                  setCarbonProvince(row);
                  setCarbonCity(null);
                }}
                placeholder="Search province…"
              />
              <SignupLocationSearch
                kind="municipalities"
                label="Municipality / city"
                selected={carbonCity}
                onSelect={setCarbonCity}
                parentFilter={
                  carbonProvince ? { province_id: locationId(carbonProvince) } : undefined
                }
                disabled={!carbonProvince}
                placeholder={
                  carbonProvince ? 'Search municipality…' : 'Select a province first…'
                }
              />
            </section>
          ) : null}

          <section className="space-y-5">
            <AuthInput
              id="password"
              label="Password"
              type="password"
              icon="ri-lock-2-line"
              value={password}
              onChange={setPassword}
              placeholder="Enter your password"
              autoComplete="new-password"
              minLength={8}
              passwordToggle
              required
              hint="At least 8 characters."
            />
            <AuthInput
              id="confirmPassword"
              label="Confirm password"
              type="password"
              icon="ri-lock-password-line"
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder="Repeat your password"
              autoComplete="new-password"
              minLength={8}
              passwordToggle
              required
            />
          </section>

          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={acceptedPolicies}
              onChange={(e) => setAcceptedPolicies(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-[#008540] focus:ring-[#008540]"
            />
            <span className="text-sm leading-6" style={{ color: AUTH_MUTED }}>
              I agree to the{' '}
              <button
                type="button"
                onClick={() => openPolicies('privacy')}
                className="font-semibold hover:underline"
                style={{ color: AUTH_GREEN }}
              >
                Privacy Policy
              </button>{' '}
              and{' '}
              <button
                type="button"
                onClick={() => openPolicies('terms')}
                className="font-semibold hover:underline"
                style={{ color: AUTH_GREEN }}
              >
                Terms &amp; Conditions
              </button>
              .
            </span>
          </label>

          <AuthButton type="submit" loading={loading} disabled={!acceptedPolicies}>
            {loading ? 'Creating account…' : 'Create account'}
          </AuthButton>
        </form>
      </AuthPageShell>
    </>
  );
}
